/**
 * Weekly preview with auto-zoom to visible hours, side-by-side overlap layout,
 * and drag-and-drop on proposed blocks.
 *
 * Props:
 *  - blocks: proposed blocks (mutable via dragging)
 *  - existingBlocks: read-only existing therapist sessions
 *  - therapists: [{therapist_id, therapist_name, therapist_role}, ...] — drives color palette
 *  - onBlockClick(block, kind): fires on click without drag movement
 *  - onBlockMove(blockIndex, {day, start, end}): fires when drag ends with a snap delta
 */
import React, { useEffect, useRef, useState, useMemo } from "react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const HOUR_HEIGHT = 64; // px per hour — large enough to read block labels
const SNAP_MIN = 30;
const PALETTE = [
  { bg: "#274f38", border: "#1E3D2B", light: "#E5EBE8" },
  { bg: "#B07C60", border: "#946750", light: "#FAF0E8" },
  { bg: "#5b7fa6", border: "#3d6080", light: "#E8EFF5" },
];
const ROLE_LABEL = { bt: "BT", program_manager: "PM", bcba: "BCBA" };

const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const fromMin = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

function overlaps(s1, e1, s2, e2) { return s1 < e2 && s2 < e1; }

// Greedy column assignment so overlapping blocks sit side-by-side
function assignColumns(blocks) {
  const sorted = [...blocks].sort((a, b) => toMin(a.start) - toMin(b.start));
  const colEnds = [];
  const assigned = sorted.map(b => {
    const s = toMin(b.start);
    let col = colEnds.findIndex(end => s >= end);
    if (col === -1) { col = colEnds.length; colEnds.push(toMin(b.end)); }
    else { colEnds[col] = toMin(b.end); }
    return { ...b, _col: col };
  });
  return assigned.map(b => ({ ...b, _totalCols: colEnds.length }));
}

export default function WeeklyPreview({ blocks = [], existingBlocks = [], therapists = [], onBlockClick, onBlockMove }) {
  // Color by therapist_id
  const therapistColor = useMemo(() => {
    const m = {};
    therapists.forEach((t, i) => { m[t.therapist_id] = PALETTE[i % PALETTE.length]; });
    return m;
  }, [therapists]);

  // Auto-zoom: compute visible hour range from all blocks (±1 hour buffer)
  const { hourStart, hourEnd, visibleDays } = useMemo(() => {
    const all = [...blocks, ...existingBlocks];
    if (!all.length) return { hourStart: 8, hourEnd: 20, visibleDays: [0,1,2,3,4] };
    const mins = all.map(b => toMin(b.start));
    const maxs = all.map(b => toMin(b.end));
    const hs = Math.max(7, Math.floor(Math.min(...mins) / 60) - 1);
    const he = Math.min(22, Math.ceil(Math.max(...maxs) / 60) + 1);
    const days = new Set(all.map(b => b.day));
    const vd = [0,1,2,3,4,5,6].filter(d => days.has(d));
    return { hourStart: hs, hourEnd: he, visibleDays: vd.length ? vd : [0,1,2,3,4] };
  }, [blocks, existingBlocks]);

  const hours = Array.from({ length: hourEnd - hourStart }, (_, i) => hourStart + i);
  const totalHeight = hours.length * HOUR_HEIGHT;
  const numDays = visibleDays.length;

  const blockTop = (t) => ((toMin(t) - hourStart * 60) / 60) * HOUR_HEIGHT;
  const blockHeight = (s, e) => Math.max(((toMin(e) - toMin(s)) / 60) * HOUR_HEIGHT, 24);

  // Conflict detection
  const conflictByIndex = useMemo(() => {
    const out = {};
    blocks.forEach((b, i) => {
      const bs = toMin(b.start), be = toMin(b.end);
      const errs = [];
      existingBlocks.forEach(ex => {
        if (ex.day !== b.day) return;
        if (ex.therapist_id && b.therapist_id && ex.therapist_id !== b.therapist_id) return;
        if (overlaps(bs, be, toMin(ex.start), toMin(ex.end))) errs.push(`${ex.client_name || "Existing"} ${ex.start}-${ex.end}`);
      });
      blocks.forEach((other, j) => {
        if (i === j || other.day !== b.day || other.therapist_id !== b.therapist_id) return;
        if (overlaps(bs, be, toMin(other.start), toMin(other.end))) errs.push(`${other.therapist_name} ${other.start}-${other.end}`);
      });
      if (errs.length) out[i] = errs;
    });
    return out;
  }, [blocks, existingBlocks]);

  const proposedByDay = useMemo(() => {
    const m = {}; visibleDays.forEach(d => { m[d] = []; });
    blocks.forEach((b, i) => { if (m[b.day] !== undefined) m[b.day].push({ ...b, _idx: i }); });
    Object.keys(m).forEach(k => { m[k] = assignColumns(m[k]); });
    return m;
  }, [blocks, visibleDays]);

  const existingByDay = useMemo(() => {
    const m = {}; visibleDays.forEach(d => { m[d] = []; });
    existingBlocks.forEach(b => { if (m[b.day] !== undefined) m[b.day].push(b); });
    return m;
  }, [existingBlocks, visibleDays]);

  // Drag state
  const gridRef = useRef(null);
  const [drag, setDrag] = useState(null);

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const rect = gridRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dx = e.clientX - drag.originX;
      const dy = e.clientY - drag.originY;
      const colWidth = rect.width / numDays;
      const dayDelta = Math.round(dx / colWidth);
      const newDayIdx = Math.max(0, Math.min(numDays - 1, drag.dayIdx + dayDelta));
      const newDay = visibleDays[newDayIdx];
      const minDelta = Math.round((dy / HOUR_HEIGHT) * 60 / SNAP_MIN) * SNAP_MIN;
      const dur = drag.endMin - drag.startMin;
      const newStart = Math.max(hourStart * 60, Math.min(hourEnd * 60 - dur, drag.startMin + minDelta));
      setDrag({ ...drag, ghost: { day: newDay, dayIdx: newDayIdx, startMin: newStart, endMin: newStart + dur }, didMove: drag.didMove || Math.abs(dx) > 3 || Math.abs(dy) > 3 });
    };
    const onUp = () => {
      if (!drag) return;
      const block = blocks[drag.idx];
      if (drag.didMove && drag.ghost && onBlockMove) {
        const ns = fromMin(drag.ghost.startMin), ne = fromMin(drag.ghost.endMin);
        if (drag.ghost.day !== block.day || ns !== block.start || ne !== block.end)
          onBlockMove(drag.idx, { day: drag.ghost.day, start: ns, end: ne });
      } else if (!drag.didMove && onBlockClick) {
        onBlockClick(block, "proposed");
      }
      setDrag(null);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); };
  }, [drag, blocks, onBlockClick, onBlockMove, numDays, visibleDays, hourStart, hourEnd]);

  const startDrag = (e, idx) => {
    e.preventDefault();
    const b = blocks[idx];
    const dayIdx = visibleDays.indexOf(b.day);
    setDrag({ idx, originX: e.clientX, originY: e.clientY, startMin: toMin(b.start), endMin: toMin(b.end), day: b.day, dayIdx, didMove: false, ghost: null });
  };

  return (
    <div className="border border-soft rounded-lg overflow-hidden bg-surface select-none" data-testid="weekly-preview">
      {/* Header */}
      <div style={{ display: "grid", gridTemplateColumns: `52px repeat(${numDays}, minmax(0,1fr))` }} className="bg-muted-soft border-b border-soft">
        <div />
        {visibleDays.map(d => (
          <div key={d} className="px-2 py-2.5 text-[11px] uppercase tracking-[0.18em] text-muted-ohana font-semibold border-l border-soft text-center">
            {DAYS[d]}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div ref={gridRef} style={{ display: "grid", gridTemplateColumns: `52px repeat(${numDays}, minmax(0,1fr))`, height: totalHeight }}>
        {/* Time labels */}
        <div className="border-r border-soft">
          {hours.map(h => (
            <div key={h} style={{ height: HOUR_HEIGHT }} className="px-2 pt-1 text-[10px] font-mono text-muted-ohana border-b border-[#EEEAE0] leading-tight">
              {String(h).padStart(2, "0")}:00
            </div>
          ))}
        </div>

        {/* Day columns */}
        {visibleDays.map((dayIdx, colPos) => {
          const proposed = proposedByDay[dayIdx] || [];
          const existing = existingByDay[dayIdx] || [];
          return (
            <div key={dayIdx} className="relative border-l border-soft">
              {/* Hour grid lines */}
              {hours.map(h => (
                <div key={h} style={{ height: HOUR_HEIGHT }} className="border-b border-[#EEEAE0]" />
              ))}

              {/* Existing (read-only) blocks */}
              {existing.map((b, i) => (
                <button
                  type="button"
                  key={`ex-${i}`}
                  onClick={() => onBlockClick && onBlockClick(b, "existing")}
                  title={`Existing: ${b.client_name} · ${b.start}–${b.end}`}
                  style={{
                    top: blockTop(b.start),
                    height: blockHeight(b.start, b.end),
                    left: 2, right: proposed.length ? "50%" : 2,
                    backgroundImage: "repeating-linear-gradient(135deg, #DCE3E0 0 6px, #E5EBE8 6px 12px)",
                    borderLeft: "3px solid #A1ACA6",
                  }}
                  className="absolute rounded text-[#586960] text-[9px] px-1.5 py-1 overflow-hidden cursor-pointer hover:ring-2 hover:ring-[#274f38]/20 text-left"
                >
                  <div className="font-semibold font-mono truncate">{b.start}–{b.end}</div>
                  <div className="truncate opacity-70 text-[8px]">{b.client_name}</div>
                </button>
              ))}

              {/* Proposed blocks — side-by-side when overlapping */}
              {proposed.map((b) => {
                const idx = b._idx;
                const isDragging = drag?.idx === idx;
                const isGhostHere = isDragging && drag.ghost?.dayIdx === colPos;
                if (isDragging && drag.ghost && drag.ghost.dayIdx !== colPos) return null;
                const display = isGhostHere
                  ? { start: fromMin(drag.ghost.startMin), end: fromMin(drag.ghost.endMin) }
                  : { start: b.start, end: b.end };
                const color = therapistColor[b.therapist_id] || PALETTE[0];
                const conflict = conflictByIndex[idx];
                const hasExist = existing.length > 0;
                const totalCols = b._totalCols || 1;
                const col = b._col || 0;
                const zoneLeft = hasExist ? 50 : 0;
                const zoneW = hasExist ? 50 : 100;
                const leftPct = zoneLeft + col * (zoneW / totalCols);
                const widthPct = zoneW / totalCols;
                const role = therapists.find(t => t.therapist_id === b.therapist_id)?.therapist_role;
                return (
                  <div
                    key={`pr-${idx}`}
                    data-testid={`preview-block-${dayIdx}-${idx}`}
                    onPointerDown={(e) => startDrag(e, idx)}
                    style={{
                      top: blockTop(display.start),
                      height: blockHeight(display.start, display.end),
                      left: `calc(${leftPct}% + 2px)`,
                      width: `calc(${widthPct}% - 4px)`,
                      backgroundColor: color.bg,
                      borderLeft: `4px solid ${color.border}`,
                      opacity: isDragging ? 0.8 : 1,
                      boxShadow: conflict ? "0 0 0 2px #B85C5C" : (isDragging ? "0 8px 20px rgba(0,0,0,0.2)" : "0 1px 3px rgba(0,0,0,0.15)"),
                      zIndex: isDragging ? 20 : 2,
                      cursor: isDragging ? "grabbing" : "grab",
                    }}
                    title={conflict ? `Conflict:\n${conflict.join("\n")}` : `${b.therapist_name} · ${display.start}–${display.end}`}
                    className="absolute rounded-md text-white overflow-hidden hover:brightness-110 transition-all touch-none px-2 py-1.5"
                  >
                    {role && (
                      <div className="text-[9px] font-bold uppercase tracking-wider text-white/70 leading-none mb-0.5">
                        {ROLE_LABEL[role] || role}
                      </div>
                    )}
                    <div className="font-semibold text-[11px] leading-tight truncate">{b.therapist_name?.split(" ")[0]}</div>
                    <div className="font-mono text-[10px] text-white/80 leading-tight">{display.start}–{display.end}</div>
                    {conflict && <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-400 ring-2 ring-white" />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
